<template>
    <section class="section categories-summary-page">
        <div class="container">
            <div class="columns">
                <div class="column">
                    <h1 class="title">{{ translate("admin_categories.title") }}</h1>
                </div>
            </div>

            <div class="columns">
                <div class="column">
                    <div class="buttons">
                        <a class="button is-primary" :href="urls.eventAdmin().categoriesManagementPage(eventId)">
                            <span class="icon">
                                <i class="fa fa-wrench" />
                            </span>
                            <span>{{ translate("admin_categories.manage") }}</span>
                        </a>
                        <a class="button" :href="urls.eventAdmin().categoriesSummaryCsv(eventId)">
                            <span class="icon">
                                <i class="fa fa-download" />
                            </span>
                            <span>{{ translate("admin_categories.download_all") }}</span>
                        </a>
                    </div>

                    <div class="mb-3">
                        <QueryInput v-model="searchQuery" />
                        <div>
                            <Checkbox2 v-model="hideEmptyCategories">
                                {{ translate("admin_categories.hide_empty_categories") }}
                            </Checkbox2>
                            <Checkbox2 v-model="onlyOneManCategories">
                                {{ translate("admin_categories.show_categories_with_one_competitor") }}
                            </Checkbox2>
                        </div>
                        <div class="mt-1">{{ translate("common.items_found") }}: {{ filteredCategories.length }}</div>
                    </div>

                    <!-- Desktop Table View -->
                    <table class="table is-hoverable is-fullwidth is-hidden-touch">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{{ translate("common.category") }}</th>
                                <th>{{ translate("common.total_competitors") }}</th>
                                <th>
                                    {{ translate("admin_categories.competitor_candidates") }}
                                </th>
                                <th>
                                    {{ translate("admin_categories.payment_disqualifications") }}
                                </th>
                                <th>
                                    {{ translate("admin_categories.disqualified_at_verification") }}
                                </th>
                                <th>
                                    {{ translate("admin_categories.competitor_verified") }}
                                </th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            <CategoryTableRow
                                v-for="(category, index) in filteredCategories"
                                :key="category.parameterId"
                                :category="category"
                                :index="index"
                                :event-id="eventId"
                                @divide-category="openDivideCategoryModal"
                                @open-category="openCategorySidebar"
                            />
                        </tbody>
                    </table>

                    <!-- Mobile Card View -->
                    <div class="is-hidden-desktop">
                        <CategoryCard
                            v-for="(category, index) in filteredCategories"
                            :key="category.parameterId"
                            :category="category"
                            :index="index"
                            :event-id="eventId"
                            @divide-category="openDivideCategoryModal"
                            @open-category="openCategorySidebar"
                        />
                    </div>
                </div>
            </div>
        </div>

        <DivideCategoryModal ref="divideCategoryModal" :event-id="eventId" @confirmed="onCategoryDivided" />

        <AdminCategorySidebar ref="categorySidebar" :event-id="eventId" :event-timezone="eventTimezone" :current-user-id="currentUserId" />
    </section>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref, watch } from "vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import QueryInput from "../Common/QueryInput.vue";
    import categoriesSummary, { CategorySummary } from "../Api/Categories/categoriesSummary";
    import objectMatcher from "../Common/objectMatcher";
    import Checkbox2 from "../Common/Checkbox2.vue";
    import urls from "../Urls/urls";
    import { translate } from "../Common/translations";
    import DivideCategoryModal from "./DivideCategoryModal.vue";
    import CategoryTableRow from "./CategoryTableRow.vue";
    import CategoryCard from "./CategoryCard.vue";
    import AdminCategorySidebar from "../Common/Fights/AdminCategorySidebar.vue";
    import history from "../Common/history";
    import { COMPETITOR_PAYMENT_DQ_STATUS } from "../Common/Competitors/types";

    const historyInstance = history.getInstance(window);

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
        currentUserId?: number | null;
    }>();

    const onlyOneManCategories = ref<boolean>(false);
    const hideEmptyCategories = ref<boolean>(false);
    const categories = ref<CategorySummary[]>();
    const searchQuery = ref<string>("");

    const filteredCategories = computed<CategorySummary[]>(() => {
        if (!categories.value) {
            return [];
        }

        return categories.value.filter(category => {
            if (hideEmptyCategories.value && category.totalCompetitors === 0) {
                return false;
            }

            const matchesString = objectMatcher.matchString(searchQuery.value, category.categoryName);

            if (!matchesString) {
                return false;
            }

            if (onlyOneManCategories.value) {
                //One man category is when there is only one competitor excluding all DQs
                return category.totalCompetitors - (category.totalCompetitorsForStatuses[COMPETITOR_PAYMENT_DQ_STATUS] || 0) === 1;
            }

            return true;
        });
    });

    onMounted(() => {
        // Initialize filters from URL query params
        searchQuery.value = historyInstance.getQueryParam("q") || "";
        hideEmptyCategories.value = historyInstance.getQueryParam("hideEmpty") === "1";
        onlyOneManCategories.value = historyInstance.getQueryParam("oneMan") === "1";

        return categoriesSummary.getCategoriesSummary(props.eventId).then(categoriesResponse => {
            categories.value = categoriesResponse;
        });
    });

    // Sync filter changes to URL query params
    watch(searchQuery, newValue => {
        historyInstance.setQueryParam("q", newValue || null);
    });

    watch(hideEmptyCategories, newValue => {
        historyInstance.setQueryParam("hideEmpty", newValue ? "1" : null);
    });

    watch(onlyOneManCategories, newValue => {
        historyInstance.setQueryParam("oneMan", newValue ? "1" : null);
    });

    //Divide category modal
    const divideCategoryModal = ref<InstanceType<typeof DivideCategoryModal>>();

    function openDivideCategoryModal(parameterId: number, categoryName: string) {
        if (divideCategoryModal.value) {
            divideCategoryModal.value.open(parameterId, categoryName);
        }
    }

    //Category sidebar
    const categorySidebar = ref<ComponentExposed<typeof AdminCategorySidebar> | null>(null);

    function openCategorySidebar(parameterId: number) {
        categorySidebar.value?.open(parameterId);
    }

    function onCategoryDivided() {
        return categoriesSummary.getCategoriesSummary(props.eventId).then(categoriesResponse => {
            categories.value = categoriesResponse;
        });
    }
</script>

<style scoped></style>
